import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";
import { Button, IconButton, Tooltip } from "@similarweb/ui-components";

import { isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import useLobSites from "app/components/compare/WebsiteQueryBar/useLobSites";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import { translate } from "app/filters/i18nFilter";
import { resolveCustomAttributes } from "app/pages/industry-analysis/home/resolveCustomAttributes";
import type { IDomain, IDomains } from "app/pages/industry-analysis/market-builder/MarketBuilder.types";
import { fetchLinesOfBusinessSegmentsByIds } from "app/pages/segments/querybar/hooks/useLinesOfBusinessQuery";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import type { ICountryObject } from "app/services/CountryService";
import {
    createLinesOfBusinessSegmentId,
    isLinesOfBusinessSegment,
    parseLinesOfBusinessSegmentId,
} from "app/services/segments/LinesOfBusinessUtils";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { allTrackers } from "app/services/track/track";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";
import { onBackButtonClick } from "app/UtilitiesAndConstants/UtilityFunctions/onBackButtonClick";
import {
    isIndustryAnalysisLimitationExceeded,
    showIndustryAnalysisMeteringHook,
} from "pro-features/pages/module start page/src/industry analysis/utils/metering/IndustryAnalysisMetering";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { BuilderContextProvider, useBuilderContext } from "./builderContext";
import { AiCustomIndustryBuilderV2 } from "./components/aiBuilderV2/AiCustomIndustryBuilderV2";
import { LeaveManualModal } from "./components/aiBuilderV2/LeaveManualModal";
import { translateWithFallback as t } from "./components/aiBuilderV2/translateWithFallback";
import SaveModal from "./components/saveModal/SaveModal";
import { SelectedDomainsList } from "./components/SelectedDomainsList/SelectedDomainsList";
import type { CreatedIndustry } from "./CustomHooks/customIndustryAgent.types";
import { useDomainSelection } from "./CustomHooks/useDomainSelection";
import { CountryFilter } from "./filters/Country/CountryFilter";
import { countTabSource } from "./services/countTabSource";
import { createMarket } from "./services/createMarket";
import { getInitialState } from "./services/getInitialState";
import { isCustomIndustryBuilderAvailable } from "./services/isCustomIndustryBuilderAvailable";
import { navigateToMarket } from "./services/navigateToIndustry";
import { updateMarket } from "./services/updateMarket";
import {
    BackButton,
    ContentWrapper,
    HeaderWrapper,
    MarketBuilderWrapper,
    SaveBtnContainer,
    StyledList,
    StyledListItem,
    Subtitle,
    TabsContainer,
    Title,
    TopRow,
    YourListWrapper,
} from "./styledComponents";
import BuilderTabs from "./tabs/BuilderTabs";
import { getTabFromIndex } from "./tabsConfig";

const enrichEntitiesWithSegmentsMeta = (entities: IDomains, customSegmentsMeta) => {
    if (!customSegmentsMeta) {
        return entities;
    }

    const segmentById = new Map();
    if (customSegmentsMeta.Segments) {
        customSegmentsMeta.Segments.forEach((s: ICustomSegment) => segmentById.set(s.id, s));
    }

    if (customSegmentsMeta.AccountSegments) {
        customSegmentsMeta.AccountSegments.forEach((s: ICustomSegment) => segmentById.set(s.id, s));
    }

    return entities.map((entity) => {
        if (entity.type !== EAssetType.Segment) {
            return entity;
        }
        const meta = segmentById.get(entity.id);
        if (!meta) {
            return entity;
        }
        return {
            ...entity,
            lastUpdated: entity.lastUpdated || meta.lastUpdated,
            segmentName: entity.segmentName || meta.segmentName,
            favicon: entity.favicon || meta.favicon,
        };
    });
};

const getSegmentsArray = async (segmentsLoading, customSegmentsMeta, initialState) => {
    if (segmentsLoading || !customSegmentsMeta) {
        return [];
    }

    const regularSegments = [];
    const lobSegmentIds = [];

    initialState.segments?.forEach((segId) => {
        if (isLinesOfBusinessSegment(segId)) {
            lobSegmentIds.push(segId);
        } else {
            // Handle regular segments (custom segments only, not predefined)
            const [segmentObj] = SegmentsUtils.getSegmentObjectByKey<ICustomSegment>(segId, {
                segments: customSegmentsMeta.Segments,
            });

            if (segmentObj) {
                const segmentData = {
                    domain: segmentObj.domain,
                    favicon: segmentObj.favicon,
                    type: EAssetType.Segment,
                    id: segmentObj.id,
                    segmentName: segmentObj.segmentName,
                    lastUpdated: segmentObj.lastUpdated,
                };
                regularSegments.push(segmentData);
            }
        }
    });

    let lobSegments = [];
    if (lobSegmentIds.length > 0) {
        try {
            const lobResponse = await fetchLinesOfBusinessSegmentsByIds(lobSegmentIds);
            lobSegments = lobResponse.Data.map((lobData) => ({
                domain: lobData.Site,
                favicon: lobData.Favicon,
                type: EAssetType.Segment,
                id: createLinesOfBusinessSegmentId(lobData.Site, lobData.Lob),
                segmentName: lobData.LobName,
            }));
        } catch (error) {
            Sentry.errorTracker({
                message: "Failed to fetch business lines segments by IDs",
                e: error,
                params: {
                    extra: {
                        lobSegmentIds,
                    },
                },
            });

            // Fallback to parsing if API fails
            lobSegmentIds.forEach((segId) => {
                const lobSegmentData = parseLinesOfBusinessSegmentId(segId);
                if (lobSegmentData) {
                    const { domain: siteDomain, businessLineName: lobName } = lobSegmentData;
                    lobSegments.push({
                        domain: siteDomain,
                        favicon: null,
                        type: EAssetType.Segment,
                        id: segId,
                        segmentName: lobName,
                    });
                }
            });
        }
    }

    return [...regularSegments, ...lobSegments];
};

const isValidDomain = (entity: IDomain): boolean => {
    const urlPattern = /\s+/;

    if ((!entity.type || entity.type === EAssetType.Website) && entity.domain) {
        return !urlPattern.test(entity.domain) || URL.canParse(entity.domain);
    }
    return true;
};

const InnerMarketBuilder: FC = () => {
    const { segmentsLoading, customSegmentsMeta } = useSelector((state) => {
        const {
            segmentsModule: { segmentsLoading, customSegmentsMeta },
        } = state;
        return { segmentsLoading, customSegmentsMeta };
    });

    const initialState = getInitialState();

    useLobSites({ shouldFetch: true });
    const { selectedTabIndex, selectedCountry, setSelectedCountry, setShowSegmentsDisclaimer } = useBuilderContext();
    const MAX_SEGMENTS_IN_CATEGORY = swSettings.components.IndustryAnalysis.resources.MaxNumOfSegments as number;
    // Editing an existing industry is the legacy wizard's job only: the agent builds a new
    // industry from a description and has no notion of amending one, so the compose screen and
    // the switch-back button are both off the table here.
    const isAiBuilderAvailable = isCustomIndustryBuilderAvailable() && !initialState.isEditMode;

    useEffect(() => {
        if (initialState.country) {
            const country = CategoryServiceUtils.getIndustryCountry(initialState.categoryObject);
            setSelectedCountry(country);
        }
    }, []);

    const [showSaveModal, setShowSaveModal] = useState(false);
    const [isSaveChangesModalDisplayed, setIsSaveChangesModalDisplayed] = useState(false);
    const [isSegmentsLoading, setIsSegmentsLoading] = useState(!!initialState.segments?.length);

    const [hasFellThrough, setHasFellThrough] = useState(false);
    const [hasArrivedForRunError] = useState(() => Boolean(proNavigator.getParams().showRunError));
    const [isLeaveManualModalOpen, setIsLeaveManualModalOpen] = useState(false);
    // The AI compose screen is the only entry point now: it's shown until the user falls
    // through to manual selection (or AI isn't available for this account at all), at which
    // point the tabs+list view takes over — with a switch-back entry point so the user isn't
    // stuck there if they came from the agentic flow.
    const showComposeFlow = isAiBuilderAvailable && !hasFellThrough;
    const showSwitchToAgenticButton = isAiBuilderAvailable && hasFellThrough;

    // if we are not on edit mode and industry limitation is exceeded the user will redirect to homepage and see a hook:
    useEffect(() => {
        if (!categoryObject) {
            isIndustryAnalysisLimitationExceeded().then((isExceeded) => {
                if (isExceeded) {
                    proNavigator
                        .go("digitalsuite_webmarketanalysis_home")
                        .then(() => showIndustryAnalysisMeteringHook());
                }
            });
        }
    }, []);

    const [segments, setSegments] = useState([]);

    useEffect(() => {
        getSegmentsArray(segmentsLoading, customSegmentsMeta, initialState).then(setSegments);
    }, [segmentsLoading, customSegmentsMeta]);

    const { categoryObject } = initialState;
    const {
        selectedEntities,
        selectEntities: selectEntitiesRaw,
        clearEntities,
        removeEntity,
        serializedSuggestedData,
        updateSuggestedEntities,
    } = useDomainSelection(enrichEntitiesWithSegmentsMeta([...initialState.domains, ...segments], customSegmentsMeta));

    const selectEntities = useCallback(
        (entities, shouldSyncFavicons = false) => {
            const normalized = enrichEntitiesWithSegmentsMeta(entities, customSegmentsMeta);
            selectEntitiesRaw(normalized, shouldSyncFavicons);
        },
        [selectEntitiesRaw, customSegmentsMeta],
    );

    const onAiIndustryCreated = useCallback(
        (_industry: CreatedIndustry, country: ICountryObject | undefined) => {
            if (country) {
                setSelectedCountry(country);
            }
        },
        [setSelectedCountry],
    );

    // Declared after useDomainSelection on purpose: the dependency array is evaluated
    // where the useCallback sits, so reading clearEntities any earlier hits it in the
    // temporal dead zone and throws before the page can render.
    //
    // The list really is discarded, which is what LeaveManualModal warns about: the agent
    // builds a new industry from a description and cannot take a hand-picked list with it,
    // so leaving one behind would quietly reappear if the user fell through again.
    const switchToAgenticFlow = useCallback(() => {
        clearEntities();
        setHasFellThrough(false);
        setIsLeaveManualModalOpen(false);
    }, [clearEntities]);

    const handleSwitchToAgenticClick = useCallback(() => {
        if (selectedEntities.length > 0) {
            setIsLeaveManualModalOpen(true);
        } else {
            switchToAgenticFlow();
        }
    }, [selectedEntities, switchToAgenticFlow]);

    // if we have segments on edit mode then this useEffect will update the segments on selectedEntities once we receive the data from customSegmentsMeta:
    useEffect(() => {
        if (!initialState.segments?.length) {
            return;
        }

        if (segmentsLoading || !customSegmentsMeta) {
            setIsSegmentsLoading(true);
            return;
        }

        void getSegmentsArray(segmentsLoading, customSegmentsMeta, initialState).then((loadedSegments) => {
            const domains = selectedEntities.filter((selectedEntity) => selectedEntity.type === EAssetType.Website);
            const combined = [...domains, ...loadedSegments];
            const normalizedCombined = enrichEntitiesWithSegmentsMeta(combined, customSegmentsMeta);
            setShowSegmentsDisclaimer(true);
            selectEntities(normalizedCombined, true);
            setIsSegmentsLoading(false);
        });
    }, [customSegmentsMeta, segmentsLoading, selectEntities]);

    const onBuildSegmentClick = () => {
        const isEmptyMarket = selectedEntities.length === 0;
        if (isEmptyMarket) {
            navigateToSegmentsWizard();
        } else {
            showSaveChangesModal();
        }
    };

    const showSaveChangesModal = () => setIsSaveChangesModalDisplayed(true);
    const hideSaveChangesModal = () => {
        setIsSaveChangesModalDisplayed(false);
        allTrackers.trackEvent("Button", "click", `nameCustomIndustrySegment/cancel`);
    };
    const saveMarket = (industryName) => {
        const country = selectedCountry.id;
        const marketPromise = categoryObject
            ? updateMarket(industryName, selectedEntities, country, categoryObject.categoryId)
            : createMarket(industryName, selectedEntities, country);
        return { marketPromise, country };
    };
    const navigateToSegmentsWizard = () => proNavigator.go("digitalsuite_segments-wizard");
    const saveChangesModalSubmit = (industryName) => {
        allTrackers.trackEvent("Button", "click", `nameCustomIndustrySegment/done/${industryName}`);
        saveMarket(industryName);
        navigateToSegmentsWizard();
    };
    const onSaveMarket = (industryName) => {
        const { marketPromise, country } = saveMarket(industryName);
        marketPromise.then(() => {
            navigateToMarket(industryName, country);
            const latestIndustry = UserCustomCategoryService.getLatestIndustry();
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `nameCustomIndustry/done/${industryName}`,
                resolveCustomAttributes(latestIndustry),
            );
        });
    };

    const onCloseModal = () => {
        allTrackers.trackEvent("Button", "click", `nameCustomIndustry/cancel`);
        setShowSaveModal(false);
    };

    const handleGoBack = onBackButtonClick(() => proNavigator.go("digitalsuite_webmarketanalysis_home"));

    const onChangeSelectedCountry = useCallback(
        (country) => {
            const selectedTabId = getTabFromIndex(selectedTabIndex)?.id;
            allTrackers.trackEvent("Button", "click", `by ${selectedTabId}/chooseCountry/${country.text}`);
            setSelectedCountry(country);
        },
        [selectedTabIndex],
    );

    const onShowSaveModal = useCallback(() => {
        const { websitesCount, industryCount } = countTabSource(selectedEntities); // sum the amount of entities for each tab (for tracking needs)
        const entitiesAmount = websitesCount + industryCount;
        const segmentsAmount = selectedEntities.filter((entity) => entity.type === EAssetType.Segment).length;
        const websitesAmount = entitiesAmount - segmentsAmount;
        allTrackers.trackEvent(
            "Button",
            "click",
            `SaveCustomIndustry/${selectedCountry.text}/${selectedEntities.length}`,
        );
        allTrackers.trackEvent("Button", "click", `${websitesCount}/${industryCount}`);
        allTrackers.trackEvent("Button", "SavedEntities", `${websitesAmount}/${segmentsAmount}`);
        setShowSaveModal(true);
    }, [selectedCountry, selectedEntities]);

    const sortedDomains = selectedEntities
        .slice()
        .sort((a, b) => (isValidDomain(a) ? 1 : isValidDomain(b) ? -1 : a.domain.localeCompare(b.domain)));
    const malformed = selectedEntities.filter((domain) => !isValidDomain(domain));
    const saveButton = (
        <div>
            <Button
                type="primary"
                isDisabled={selectedEntities.length === 0 || malformed.length !== 0}
                onClick={onShowSaveModal}
            >
                {translate("research.marketbuilder.save.button")}
            </Button>
        </div>
    );
    const tooltipContent =
        malformed.length === 1 ? (
            translate("market.builder.tooltip.error.singular", { record: malformed[0].domain })
        ) : (
            <div>
                {translate("market.builder.tooltip.error.plural", { count: malformed.length })}
                <StyledList>
                    {(malformed.length <= 6 ? malformed : malformed.slice(0, 5)).map((domain) => (
                        <StyledListItem>
                            {domain.domain.substring(0, 30) + (domain.domain.length > 27 ? "..." : "")}
                        </StyledListItem>
                    ))}
                </StyledList>
                {malformed.length > 6
                    ? translate("market.builder.tooltip.error.more", { count: malformed.length - 5 })
                    : null}
            </div>
        );
    return (
        <MarketBuilderWrapper data-automation="MarketBuilder">
            <BackButton
                iconName="arrow-left"
                type="flat"
                onClick={handleGoBack}
                data-automation-btn="back-btn"
                width={"fit-content"}
            >
                {translate("common.back")}
            </BackButton>

            <TopRow>
                <HeaderWrapper>
                    <Title>{translate("market.builder.title")}</Title>
                    {/* The compose/generating cards carry their own heading copy, so the page
                        subtitle would be redundant — it only applies to the shared tabs view. */}
                    {!showComposeFlow && <Subtitle>{translate("market.builder.subtitle")}</Subtitle>}
                </HeaderWrapper>
                {showSwitchToAgenticButton && (
                    <IconButton
                        type="outlined"
                        iconName="sparkles-ai"
                        iconSize="xs"
                        data-automation="market-builder-v2-switch-to-agentic-btn"
                        onClick={handleSwitchToAgenticClick}
                    >
                        {t("market.builder.v2.switchToAgentic.button", "Let agent find domains")}
                    </IconButton>
                )}
            </TopRow>

            {showComposeFlow && (
                <AiCustomIndustryBuilderV2
                    selectedCountry={selectedCountry}
                    onSelectedCountryChange={onChangeSelectedCountry}
                    onRestoreCountry={setSelectedCountry}
                    onIndustryCreated={onAiIndustryCreated}
                    onFallThrough={() => setHasFellThrough(true)}
                    onClose={handleGoBack}
                    hasArrivedForRunError={hasArrivedForRunError}
                />
            )}

            {!showComposeFlow && (
                <ContentWrapper>
                    <TabsContainer>
                        <BuilderTabs
                            selectedEntities={selectedEntities}
                            selectEntities={selectEntities}
                            serializedSuggestedData={serializedSuggestedData}
                            updateSuggestedEntities={updateSuggestedEntities}
                            onBuildSegmentClick={onBuildSegmentClick}
                            isSegmentsLoading={isSegmentsLoading}
                        />
                    </TabsContainer>
                    <YourListWrapper>
                        <SelectedDomainsList
                            domains={sortedDomains}
                            domainValidator={isValidDomain}
                            maxAllowedDomains={MAX_DOMAINS_IN_CATEGORY}
                            maxAllowedSegments={MAX_SEGMENTS_IN_CATEGORY}
                            onRemoveEntity={removeEntity}
                            isLoading={isSegmentsLoading}
                            headerExtra={
                                <CountryFilter
                                    height={32}
                                    onChangeSelectedCountry={onChangeSelectedCountry}
                                    selectedCountry={selectedCountry}
                                />
                            }
                        />
                        <SaveBtnContainer data-automation="save-custom-industry-button">
                            {malformed.length === 0 ? (
                                saveButton
                            ) : (
                                <Tooltip content={tooltipContent}>{saveButton}</Tooltip>
                            )}
                        </SaveBtnContainer>
                    </YourListWrapper>
                </ContentWrapper>
            )}
            <LeaveManualModal
                isOpen={isLeaveManualModalOpen}
                domainCount={selectedEntities.length}
                onStay={() => setIsLeaveManualModalOpen(false)}
                onLeave={switchToAgenticFlow}
            />
            <SaveModal
                isOpen={showSaveModal}
                onCloseModal={onCloseModal}
                onDone={onSaveMarket}
                category={categoryObject}
                textKeys={{ title: "research.marketbuilder.modal.title" }}
            />
            <SaveModal
                isOpen={isSaveChangesModalDisplayed}
                onCloseModal={hideSaveChangesModal}
                onDone={saveChangesModalSubmit}
                category={categoryObject}
                textKeys={{
                    title: translate("market.builder.save.changes"),
                    banner: translate("market.builder.save.changes.banner"),
                }}
            />
        </MarketBuilderWrapper>
    );
};

const MarketBuilder: FC = () => {
    const isCreatingCustomIndustryHooked = isLocked(swSettings.components.IndustryAnalysisMarketBuilder);

    if (isCreatingCustomIndustryHooked) {
        proNavigator.go("digitalsuite-home");
    }

    return (
        <BuilderContextProvider>
            <InnerMarketBuilder />
        </BuilderContextProvider>
    );
};

export default MarketBuilder;
